import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  Clock,
  Clock3,
  CheckCircle2,
  Repeat,
  Search,
  SortAsc,
  SortDesc,
  AlertCircle,
  X,
} from 'lucide-react';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { useAuth } from '../../../../context/AuthContext';
import { getStudentDashboard } from '../../../../services/StudentDashboardService';
import { getStudentOptions } from '../../../../services/StudentService';
import { getBatchChangeRequests } from '../../../../services/BatchChangeRequestService';
import CustomAsyncDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import { Skeleton, SkeletonGroup } from '../../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../../common/Loader/busy';
import RequestBatchChangeModal from '../Dashboard/BatchChangeRequest/RequestBatchChangeModal.jsx';

/* The request dialog's .bcm- skin lives in the dashboard's stylesheet;
   this page opens the same dialog, so it pulls that file for it. Both
   are scoped under .dashboard-wrapper and separately prefixed. */
import '../../Trainer/TrainerDashboard.css';
import '../Dashboard/StudentDashboard.css';
import './SuggestedBatches.css';

const SuggestedBatches = () => {
  const { user: authUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [existingRequests, setExistingRequests] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);
  const [selectedStudentEmail, setSelectedStudentEmail] = useState('');

  // Toolbar state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMode, setSelectedMode] = useState('ALL');
  const [sortDirection, setSortDirection] = useState('asc');
  const [requestTarget, setRequestTarget] = useState(null);

  // Admin student switcher state
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  const isFetchingRef = useRef(false);
  const lastFetchedIdRef = useRef(null);
  const lastFetchedEmailRef = useRef(null);
  const isAdminRef = useRef(false);
  const authUserRef = useRef(authUser);
  const isFetchingStudentsRef = useRef(false);

  const checkIfAdmin = (userData) => {
    if (!userData) return false;
    if (userData.isSuperAdmin) return true;
    return (userData.profiles || []).some(
      (p) => String(p?.label || p?.name || '').toLowerCase() === 'admin'
    );
  };

  const fetchStudents = useCallback(async (search = '', pageNum = 1) => {
    try {
      isFetchingStudentsRef.current = true;
      // Names only: the picker and the pick read id, name and email.
      const response = await getStudentOptions(10, pageNum, search);
      const studentsList = response.data || [];
      const hasMore = studentsList.length === 10;
      return { students: studentsList, hasMore };
    } catch (err) {
      console.error('Error fetching students:', err);
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudentsRef.current = false;
    }
  }, []);

  const loadStudents = useCallback(
    async (search = '', pageNum = 1, append = false) => {
      setLoadingStudents(true);
      const { students: studentsList, hasMore } = await fetchStudents(search, pageNum);

      const newOptions = studentsList.map((s) => ({
        value: s.id,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));

      if (append) {
        setStudentOptions((prev) => [...prev, ...newOptions]);
      } else {
        setStudentOptions(newOptions);
      }

      setHasMoreStudents(hasMore);
      setLoadingStudents(false);
      return { students: studentsList, hasMore };
    },
    [fetchStudents]
  );

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetchingStudentsRef.current) return;
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [hasMoreStudents, loadingStudents, studentPage, studentSearchTerm, loadStudents]);

  const handleStudentSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setStudentSearchTerm(inputValue);
        setStudentPage(1);
        await loadStudents(inputValue, 1, false);
      }, 700),
    [loadStudents]
  );

  const handleDropdownOpen = useCallback(async () => {
    if (isAdminRef.current && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents('', 1, false);
    }
  }, [studentOptions.length, loadingStudents, loadStudents]);

  const handleRefreshStudents = useCallback(async () => {
    setStudentSearchTerm('');
    setStudentPage(1);
    await loadStudents('', 1, false);
  }, [loadStudents]);

  const fetchDashboardData = useCallback(async (fetchParams = {}) => {
    const studentId = fetchParams.studentId || null;
    const email = fetchParams.email || null;

    const effectiveId = studentId || (email ? null : authUserRef.current?.id || authUserRef.current?._id);
    const effectiveEmail = email || (studentId ? null : authUserRef.current?.email);

    const isFetchingSame =
      isFetchingRef.current &&
      lastFetchedIdRef.current === effectiveId &&
      lastFetchedEmailRef.current === effectiveEmail;

    if (isFetchingSame && !fetchParams.force) return;

    try {
      // The page shows its own skeleton, so the global overlay stays down.
      silenceFor();
      setLoading(true);
      isFetchingRef.current = true;
      lastFetchedIdRef.current = effectiveId;
      lastFetchedEmailRef.current = effectiveEmail;

      const params = {};
      if (studentId) params.studentId = studentId;
      if (email) params.email = email;

      const apiRes = await getStudentDashboard(params);
      const payload = apiRes?.data || {};

      setDashboardData(payload);

      const viewer = payload.viewer || authUserRef.current;
      const adminStatus = checkIfAdmin(viewer);
      if (viewer) {
        setIsAdmin(adminStatus);
        isAdminRef.current = adminStatus;
      }

      if (adminStatus && !studentId && !email) {
        setLoading(false);
        return;
      }

      const student = payload.student || null;
      const studentIdValue = student?.id ? String(student.id) : null;
      const effectiveStudentId = studentIdValue || studentId || null;

      if (studentIdValue) {
        setSelectedStudentId(studentIdValue);
        setSelectedStudentName(student?.name || '');
        setSelectedStudentEmail(student?.email || '');

        if (student) {
          setSelectedStudentValue({
            value: student.id,
            label: `${student.name} | ${student.email}`,
            student: student,
          });
        }
      }

      if (effectiveStudentId) {
        try {
          const reqRes = await getBatchChangeRequests({ limit: 100, page: 1, studentId: effectiveStudentId });
          setExistingRequests(reqRes.data || []);
        } catch (reqErr) {
          console.error('Failed to load batch change requests for status checking:', reqErr);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching suggested batches:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (authUser) {
      authUserRef.current = authUser;
      const status = checkIfAdmin(authUser);
      setIsAdmin(status);
      isAdminRef.current = status;

      const searchParams = new URLSearchParams(location.search);
      const emailParam = searchParams.get('email');

      if (status) {
        if (emailParam && !lastFetchedEmailRef.current) {
          setSelectedStudentEmail(emailParam);
          fetchDashboardData({ email: emailParam });
        } else {
          setLoading(false);
        }
      } else if (!lastFetchedIdRef.current && !isFetchingRef.current) {
        fetchDashboardData();
      }
    }
  }, [authUser, fetchDashboardData, location.search]);

  const handleStudentSelection = useCallback(
    async (selectedOptions) => {
      const selectedOption = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;

      if (!selectedOption) {
        setSelectedStudentId(null);
        setSelectedStudentName('');
        setSelectedStudentEmail('');
        setSelectedStudentValue(null);
        setDashboardData(null);
        return;
      }

      const { value: studentId, student } = selectedOption;
      setSelectedStudentId(studentId);
      setSelectedStudentName(student?.name || '');
      setSelectedStudentEmail(student?.email || '');
      setSelectedStudentValue(selectedOption);

      if (student?.email) {
        await fetchDashboardData({ email: student.email, force: true });
      } else {
        await fetchDashboardData({ studentId, force: true });
      }
    },
    [fetchDashboardData]
  );

  const handleClearSelection = () => {
    setSelectedStudentId(null);
    setSelectedStudentName('');
    setSelectedStudentEmail('');
    setSelectedStudentValue(null);
    setDashboardData(null);
    lastFetchedIdRef.current = null;
    lastFetchedEmailRef.current = null;
  };

  const myBatches = dashboardData?.batches || [];
  const rawSuggestedBatches = dashboardData?.suggestedBatches || [];

  // Same courseId shape every batch object here uses — see the extraction
  // inline below. Used to tell "switch" (student has a batch in this course
  // already) from "join" (they don't — RequestBatchChangeModal's own
  // isJoinRequest handles the actual request; this just picks the right
  // label to show before they even open it).
  const extractCourseId = (batch) =>
    Array.isArray(batch?.courseId) && batch.courseId.length > 0
      ? (batch.courseId[0]?.id || batch.courseId[0]?._id || (typeof batch.courseId[0] === 'string' ? batch.courseId[0] : null))
      : (batch?.courseId?._id || batch?.courseId?.id || batch?.courseId || batch?.courseDetails?._id || batch?.courseDetails?.id || null);
  const hasBatchForCourse = (courseId) =>
    Boolean(courseId) && myBatches.some((b) => String(extractCourseId(b)) === String(courseId));

  const filteredSuggestedBatches = useMemo(() => {
    return rawSuggestedBatches
      .filter((batch) => {
        const courseName = Array.isArray(batch.courseId) && batch.courseId.length > 0
          ? batch.courseId[0].name
          : (batch.courseDetails?.name || '');
        const batchName = batch.name || '';
        const matchesSearch =
          batchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          courseName.toLowerCase().includes(searchTerm.toLowerCase());

        const mode = (batch.mode || batch.batchType || batch.type || 'Online').toUpperCase().trim();
        const matchesMode = selectedMode === 'ALL' || mode === selectedMode;

        return matchesSearch && matchesMode;
      })
      .sort((a, b) => {
        const dateA = new Date(a.startTime || a.startDate).getTime();
        const dateB = new Date(b.startTime || b.startDate).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
  }, [rawSuggestedBatches, searchTerm, selectedMode, sortDirection]);

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleModalSubmitted = () => {
    // No toast here: the dialog confirms the sent request in its own done panel.
    // Refresh suggested batches list
    if (selectedStudentEmail) {
      fetchDashboardData({ email: selectedStudentEmail, force: true });
    } else if (selectedStudentId) {
      fetchDashboardData({ studentId: selectedStudentId, force: true });
    } else {
      fetchDashboardData({ force: true });
    }
  };

  const getBatchRequestStatus = (batch) => {
    if (!batch || !existingRequests || existingRequests.length === 0) return null;
    const batchId = String(batch._id || batch.id);
    const match = existingRequests.find((r) => {
      const toId = String(r.toBatch?._id || r.toBatch?.id || r.toBatchId || '');
      return toId === batchId && (r.status === 'pending' || r.status === 'approved');
    });
    return match ? match.status : null;
  };

  /* One dropdown, rendered into the gate before a student is picked
     and into the picker strip afterwards. */
  const studentPicker = (
    <CustomAsyncDropdown
      inputId="sb-student-picker"
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name, email or phone..."
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleDropdownOpen}
      onRefresh={handleRefreshStudents}
      isDisabled={false}
      loadingMessage="Loading students..."
      noOptionsMessage="No students found"
      noSearchResultsMessage="No students matched your search"
    />
  );

  /* The dialog both views open. Rendered once, at the foot of each
     branch, so a click on either view's action opens the same thing. */
  const requestModal = (
    <RequestBatchChangeModal
      show={!!requestTarget}
      onClose={() => setRequestTarget(null)}
      myBatches={myBatches}
      studentId={selectedStudentId}
      presetToBatch={requestTarget}
      onSubmitted={handleModalSubmitted}
    />
  );

  /* Everything a card or a row needs, read out of a batch once. */
  const readBatch = (batch) => {
    const startDate = new Date(batch.startTime || batch.startDate);
    const endDate = new Date(batch.endTime || batch.endDate);
    const courseId = extractCourseId(batch);
    const courseName =
      Array.isArray(batch.courseId) && batch.courseId.length > 0
        ? batch.courseId[0]?.name || batch.courseId[0]?.courseName || 'Course'
        : batch.courseDetails?.name || batch.courseDetails?.courseName || 'Course';

    return {
      id: batch._id || batch.id,
      courseId,
      courseName,
      batchName: batch.name || 'Batch',
      mode: (batch.mode || batch.batchType || batch.type || 'Online').trim(),
      initials:
        (courseName || 'B').trim().split(/\s+/).filter(Boolean).slice(0, 2)
          .map((w) => w[0].toUpperCase()).join('') || 'B',
      startDate,
      endDate,
      startTime: batch.startTime ? moment(batch.startTime).format('hh:mm A') : 'N/A',
      endTime: batch.endTime ? moment(batch.endTime).format('hh:mm A') : 'N/A',
      requestStatus: getBatchRequestStatus(batch),
      isSwitch: hasBatchForCourse(courseId),
    };
  };

  const fmtDay = (d) =>
    Number.isNaN(d?.getTime?.())
      ? '—'
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const MODES = ['ALL', 'ONLINE', 'OFFLINE', 'HYBRID'];

  /* ── Staff with nobody selected ── */
  if (isAdmin && !selectedStudentId) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sb-gate">
            <span className="sb-gate-mark">
              <Repeat size={22} aria-hidden="true" />
            </span>
            <h1 className="sb-gate-title">Pick a student</h1>
            <p className="sb-gate-text">
              Choose a student to see the batches they could move into.
            </p>
            <div className="sb-gate-field">{studentPicker}</div>
            <span className="sb-gate-tip">
              <AlertCircle size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        </div>
      </div>
    );
  }

  /* =============================================================
     STAFF VIEW — the shared kit, read as a list
     ============================================================= */
  if (isAdmin) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sb-picker">
            <div className="sb-picker-id">
              <span className="sb-avatar">
                {(selectedStudentName || 'S').trim().charAt(0).toUpperCase()}
              </span>
              <div>
                <div className="sb-picker-name">{selectedStudentName || 'Student'}</div>
                <div className="sb-picker-mail">
                  {selectedStudentEmail || 'No email on record'}
                </div>
              </div>
            </div>

            <div className="sb-picker-field">
              <label className="sb-picker-label" htmlFor="sb-student-picker">Viewing student</label>
              {studentPicker}
            </div>

            <button type="button" className="sb-clear" onClick={handleClearSelection}>
              <X size={13} aria-hidden="true" />
              Clear
            </button>
          </div>

          <section className="td-tbl td-tbl--sblist">
            <header className="td-tbl-head">
              <div>
                <h5 className="td-tbl-title">Batches available to switch into</h5>
                <p className="td-tbl-sub">
                  {filteredSuggestedBatches.length} of {rawSuggestedBatches.length} alternate
                  batch{rawSuggestedBatches.length === 1 ? '' : 'es'}
                </p>
              </div>
            </header>

            <div className="td-bar">
              <div className="sb-picker-field">
                <div className="sb-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    type="text"
                    id="sb-staff-search"
                    name="batchSearch"
                    value={searchTerm}
                    placeholder="Search batch or course"
                    aria-label="Search batches"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="sb-modes">
                {MODES.map((mode) => (
                  <button
                    type="button"
                    key={mode}
                    className={`sb-mode${selectedMode === mode ? ' is-on' : ''}`}
                    aria-pressed={selectedMode === mode}
                    onClick={() => setSelectedMode(mode)}
                  >
                    {mode === 'ALL' ? 'All modes' : mode.charAt(0) + mode.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              /* Rows of the table itself, on its five tracks (--sblist). */
              <SkeletonGroup label="Loading batches" className="td-tbl-rows">
                {[0, 1, 2, 3].map((i) => (
                  <div className="td-tbl-row" key={i}>
                    <div className="td-tbl-name lms-skel-stack">
                      <Skeleton width="70" />
                      <Skeleton width="40" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton width="55" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton width="55" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton width="40" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton shape="pill" />
                    </div>
                  </div>
                ))}
              </SkeletonGroup>
            ) : rawSuggestedBatches.length === 0 ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <Repeat size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">No alternates available</h6>
                <p className="td-state-text">
                  There is no other batch running for the courses this student is enrolled in.
                </p>
              </div>
            ) : filteredSuggestedBatches.length === 0 ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <Search size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">Nothing matches</h6>
                <p className="td-state-text">
                  No batch matches that search or mode. Clear the filters to see all
                  {' '}{rawSuggestedBatches.length}.
                </p>
              </div>
            ) : (
              <>
                {/* Five labels against five tracks in --sblist. */}
                <div className="td-tbl-cols">
                  <span>Batch</span>
                  <span>Course</span>
                  <span>Mode</span>
                  <span>Starts</span>
                  <span>Request</span>
                </div>

                <div className="td-tbl-rows">
                  {filteredSuggestedBatches.map((batch, index) => {
                    const b = readBatch(batch);
                    return (
                      <div className="td-tbl-row" key={b.id || index}>
                        <div className="td-tbl-name">
                          <b>{b.batchName}</b>
                          <small>
                            {b.startTime} – {b.endTime}
                          </small>
                        </div>
                        <div className="td-tbl-name">
                          <b>{b.courseName}</b>
                        </div>
                        <span className="td-tbl-pill">{b.mode}</span>
                        <div className="td-tbl-name">
                          <b>{fmtDay(b.startDate)}</b>
                          <small>to {fmtDay(b.endDate)}</small>
                        </div>

                        {b.requestStatus === 'pending' ? (
                          <button type="button" className="sb-row-btn" disabled>
                            <Clock3 size={13} aria-hidden="true" />
                            Pending review
                          </button>
                        ) : b.requestStatus === 'approved' ? (
                          <button type="button" className="sb-row-btn" disabled>
                            <CheckCircle2 size={13} aria-hidden="true" />
                            Approved
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="sb-row-btn"
                            onClick={() =>
                              setRequestTarget({
                                id: b.id,
                                name: b.batchName,
                                courseId: b.courseId,
                              })
                            }
                          >
                            <Repeat size={13} aria-hidden="true" />
                            {b.isSwitch ? 'Request switch' : 'Request join'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>

        {requestModal}
      </div>
    );
  }

  /* =============================================================
     STUDENT VIEW — the batches as cards
     ============================================================= */
  return (
    <div className="dashboard-wrapper">
      <div className="sb">
        <div className="sb-wrap">
          <div>
            <div className="sb-eyebrow">Change your schedule</div>
            <h1 className="sb-title">Batches you could move into</h1>
            <p className="sb-lede">
              These run the same courses you are enrolled in, on a different schedule.
              Requesting a switch sends it to your coordinator — nothing changes until they
              approve it.
            </p>
          </div>

          <div className="sb-tools">
            <div className="sb-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="text"
                id="sb-search"
                name="batchSearch"
                value={searchTerm}
                placeholder="Search by batch or course"
                aria-label="Search batches"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="sb-search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="sb-tools-row">
              <div className="sb-modes">
                {MODES.map((mode) => (
                  <button
                    type="button"
                    key={mode}
                    className={`sb-mode${selectedMode === mode ? ' is-on' : ''}`}
                    aria-pressed={selectedMode === mode}
                    onClick={() => setSelectedMode(mode)}
                  >
                    {mode === 'ALL' ? 'All modes' : mode.charAt(0) + mode.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>

              <button type="button" className="sb-sort" onClick={toggleSortDirection}>
                {sortDirection === 'asc' ? <SortAsc size={13} /> : <SortDesc size={13} />}
                {sortDirection === 'asc' ? 'Soonest first' : 'Latest first'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="sb-grid">
              {[0, 1, 2].map((i) => (
                <div className="sb-skel" key={i}>
                  <div className="sb-skel-bar sb-skel-bar--third" />
                  <div className="sb-skel-bar" />
                  <div className="sb-skel-bar sb-skel-bar--half" />
                </div>
              ))}
            </div>
          ) : rawSuggestedBatches.length === 0 ? (
            <div className="sb-state">
              <span className="sb-state-mark">
                <Repeat size={24} aria-hidden="true" />
              </span>
              <h2 className="sb-state-title">No alternate batches right now</h2>
              <p className="sb-state-text">
                There is no other batch running for your courses at the moment. This page
                fills up as new batches are scheduled.
              </p>
            </div>
          ) : filteredSuggestedBatches.length === 0 ? (
            <div className="sb-state">
              <span className="sb-state-mark">
                <Search size={24} aria-hidden="true" />
              </span>
              <h2 className="sb-state-title">Nothing matches</h2>
              <p className="sb-state-text">
                No batch matches that search or mode.
              </p>
              <button
                type="button"
                className="sb-state-btn"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedMode('ALL');
                }}
              >
                <X size={14} aria-hidden="true" />
                Clear filters
              </button>
            </div>
          ) : (
            <div className="sb-grid">
              {filteredSuggestedBatches.map((batch, index) => {
                const b = readBatch(batch);
                return (
                  <article className="sb-card" key={b.id || index}>
                    <div className="sb-card-head">
                      <span className="sb-mark">{b.initials}</span>
                      <div className="sb-card-id">
                        <h2 className="sb-course">{b.courseName}</h2>
                        <p className="sb-batch">{b.batchName}</p>
                      </div>
                    </div>

                    <div className="sb-chips">
                      <span className="sb-chip">{b.mode}</span>
                      {b.requestStatus === 'pending' && (
                        <span className="sb-chip sb-chip--wait">Request pending</span>
                      )}
                      {b.requestStatus === 'approved' && (
                        <span className="sb-chip sb-chip--done">Approved</span>
                      )}
                    </div>

                    <ul className="sb-facts">
                      <li>
                        <Calendar size={13} aria-hidden="true" />
                        <span className="sb-fact-text">
                          {fmtDay(b.startDate)} – {fmtDay(b.endDate)}
                        </span>
                      </li>
                      <li>
                        <Clock size={13} aria-hidden="true" />
                        <span className="sb-fact-text">
                          {b.startTime} – {b.endTime}
                        </span>
                      </li>
                      <li>
                        <BookOpen size={13} aria-hidden="true" />
                        <span className="sb-fact-text">
                          {b.startDate.toLocaleDateString('en-GB', {
                            month: 'long',
                            year: 'numeric',
                          })}{' '}
                          batch
                        </span>
                      </li>
                    </ul>

                    <div className="sb-act">
                      {b.requestStatus === 'pending' ? (
                        <button type="button" className="sb-btn" disabled>
                          <Clock3 size={14} aria-hidden="true" />
                          Request under review
                        </button>
                      ) : b.requestStatus === 'approved' ? (
                        <button type="button" className="sb-btn" disabled>
                          <CheckCircle2 size={14} aria-hidden="true" />
                          Approved
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="sb-btn"
                          onClick={() =>
                            setRequestTarget({
                              id: b.id,
                              name: b.batchName,
                              courseId: b.courseId,
                            })
                          }
                        >
                          <Repeat size={14} aria-hidden="true" />
                          {b.isSwitch ? 'Request to switch' : 'Request to join'}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {requestModal}
    </div>
  );
};

export default SuggestedBatches;
